// Pure TypeScript. No React. No imports from features or services.
//
// Minimal RFC-5545 VEVENT generator for the calendar (.ics) fallback so a
// guest who closes the tab doesn't have to babysit the lobby (§4.3), and a
// salesperson can drop a scheduled meeting straight onto their calendar.

function toICSDate(date: Date): string {
  // UTC basic format: 20260704T143000Z
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function escapeText(value: string): string {
  return value.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

export interface ICSEvent {
  uid: string;
  title: string;
  start: Date;
  /** Defaults to start + 30 min. */
  end?: Date;
  description?: string;
  url?: string;
}

export function buildICS(event: ICSEvent): string {
  const end = event.end ?? new Date(event.start.getTime() + 30 * 60_000);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NSI Meet//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(event.start)}`,
    `DTEND:${toICSDate(end)}`,
    `SUMMARY:${escapeText(event.title)}`,
    event.description ? `DESCRIPTION:${escapeText(event.description)}` : '',
    event.url ? `URL:${escapeText(event.url)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  return lines.join('\r\n');
}

/** Trigger a client-side download of an .ics file. */
export function downloadICS(filename: string, event: ICSEvent): void {
  const blob = new Blob([buildICS(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
